import type { FormEvent, PropsWithChildren, ReactElement } from 'react'
import { clamp, debounce, memoize, omit } from 'lodash-es'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import Immutable from 'immutable'
import cc from 'classcat'

import { generateProductUrl, generateSearchUrl } from '../../urlGenerators'
import { updateSuggestedSearch, updateSuggestedSearchSelection } from '../../store/actions'
import { useApi } from '../ApiProvider'
import { useStickyHeaderContext } from '../../utils/StickyHeaderContext'
import { useTranslate } from '../../utils/translate'
import { withQueryAndScope } from '../../urlGeneratorsPlain'
import createPromiseLinearizer from '../../utils/createPromiseLinearizer'
import delay from '../../utils/delay'
import focusDocumentBody from '../../utils/focusDocumentBody'

const KeyCodes = {
  ESCAPE: 27,
  RETURN: 13,
  UP: 38,
  DOWN: 40,
}

export const NamespaceContext = createContext<string | undefined>(undefined)

export function useNamespace() {
  const namespace = useContext(NamespaceContext)

  if (!namespace) {
    throw new Error(
      'NamespaceContext value is undefined. Make sure you use this hook within a NamespaceContext.Provider component',
    )
  }

  return namespace
}

export type SuggestedSearchProps = Omit<React.FormHTMLAttributes<any>, 'onSubmit'> &
  PropsWithChildren<{
    namespace: string
    focusClassName?: string
    blurDelay?: number
    onSubmit?: (event: FormEvent<HTMLFormElement>, performSearch: (query: string) => void) => void
  }>

export function SuggestedSearch({
  namespace,
  blurDelay,
  focusClassName = 'focused',
  className,
  children,
  onSubmit,
  ...rest
}: Readonly<SuggestedSearchProps>) {
  const [isFocused, setIsFocused] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<GlobalDispatch>()
  const ref = useRef<HTMLFormElement>(null)

  const reset = useCallback(() => {
    const maybeDelaying = blurDelay ? delay(blurDelay) : Promise.resolve()

    maybeDelaying.then(() => {
      // Reset with the value `null` to differentiate between a programmatic
      // and a user search form reset (which would be an empty string)
      dispatch(updateSuggestedSearch(namespace, null, null))
      dispatch(updateSuggestedSearchSelection(namespace, null))
      setIsFocused(false)
    })
  }, [blurDelay, dispatch, namespace])

  const { isStickyHeaderRevealedOnScrollUp = false } = useStickyHeaderContext() ?? {}
  const wasRevealedOnScrollUp = useRef(isStickyHeaderRevealedOnScrollUp)

  useEffect(() => {
    if (wasRevealedOnScrollUp.current && !isStickyHeaderRevealedOnScrollUp) reset()
    wasRevealedOnScrollUp.current = isStickyHeaderRevealedOnScrollUp
  }, [isStickyHeaderRevealedOnScrollUp, reset])

  const handleFocus = () => !isFocused && setIsFocused(true)

  const handleBlur = (event) => {
    // Reset only if blurred out of the container element.
    // (`event.relatedTarget` is the DOM element which received focus.)
    if (!event.relatedTarget || !ref.current?.contains(event.relatedTarget)) reset()
  }

  const handleClick = (event) => {
    // Reset if clicked on a link of a suggested item.
    if (event.target.closest('a')) {
      reset()
      focusDocumentBody()
    }
  }

  const performSearch = (query: string) => {
    reset()
    // open the search with the search query
    navigate(withQueryAndScope(generateSearchUrl(query), location))
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => onSubmit?.(event, performSearch)

  // tabIndex="-1" makes the element focusable (but not reachable via keyboard navigation),
  // so that we can figure out if blurred out of the container in the blur event handler.
  return (
    <NamespaceContext.Provider value={namespace}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <form
        {...rest}
        ref={ref}
        tabIndex={-1}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onSubmit={handleSubmit}
        className={cc([
          className,
          {
            [focusClassName]: isFocused,
          },
        ])}
      >
        {children}
      </form>
    </NamespaceContext.Provider>
  )
}

type SearchFieldProps = {
  maxSuggestedItems?: number
  placeholder?: string
} & React.InputHTMLAttributes<HTMLInputElement>

export function SearchField({
  // 10 is the jREST default limit
  maxSuggestedItems = 10,
  className,
  placeholder,
  ...rest
}: Readonly<SearchFieldProps>) {
  const t = useTranslate('shop')
  const namespace = useNamespace()
  const dispatch = useDispatch<GlobalDispatch>()

  const updateSearch = useCallback(
    (searchTerm: string, results: Immutable.List<ImmutableMap> | null) =>
      dispatch(updateSuggestedSearch(namespace, searchTerm, results)),
    [dispatch, namespace],
  )

  const updateSelection = (index: number | null) => dispatch(updateSuggestedSearchSelection(namespace, index))

  const locale = useSelector<State, string>((state) => state.getIn(['shop', 'locale']))
  const searchTerm = useSelector<State, string>((state) =>
    state.getIn(['view', 'suggestedSearch', namespace, 'searchTerm'], ''),
  )
  const results = useSelector<State, ImmutableList | undefined>((state) =>
    state.getIn(['view', 'suggestedSearch', namespace, 'results']),
  )
  const selected = useSelector<State, number>((state) =>
    state.getIn(['view', 'suggestedSearch', namespace, 'selected']),
  )

  const location = useLocation()
  const navigate = useNavigate()
  const ref = useRef<HTMLInputElement>(null)
  const api = useApi()

  const [query, setQuery] = useState(searchTerm)
  const [emptyQueryError, setEmptyQueryError] = useState(false)

  useEffect(() => {
    setQuery(searchTerm)
  }, [searchTerm])

  useEffect(() => {
    if (query === null) {
      // Start over from programmatic reset (indicated by the value `null`)
      updateSearch('', null)
    }
  }, [query, updateSearch])

  const resetSearch = () => {
    dispatch(updateSuggestedSearch(namespace, '', null))
    dispatch(updateSuggestedSearchSelection(namespace, null))
    setQuery('')
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendSearch = useCallback(
    memoize(
      (query) => {
        return api.get('/api/v2/suggestedSearch', {
          params: {
            q: query,
            limit: maxSuggestedItems,
            locale,
          },
        })
      },
      (query) => `${locale}-${query}`,
    ),
    [api, locale, maxSuggestedItems],
  )

  // Note: Might be refactored with `useFetcher()`
  const linearizer = createPromiseLinearizer()

  const search = (query: string) => {
    if (query.length > 1) {
      return linearizer(() => sendSearch(query))
        .then((res) => Immutable.fromJS(res.data))
        .then((products) =>
          products.map((product) => {
            return product.set('href', generateProductUrl(product.toJS())).set('active', false)
          }),
        )
    }

    return Promise.resolve(null)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateQuery = useCallback(
    debounce((currentQuery) => {
      search(currentQuery).then((products) => {
        if (ref.current === document.activeElement && currentQuery === ref.current?.value) {
          updateSearch(currentQuery, products)
          if (selected) updateSelection(null)
        }
      })
    }, 800),
    [],
  )

  const getResults = () => {
    const selectedItem = Number.isInteger(selected) ? results?.get(selected)?.toJS() : undefined

    const trimmedQuery = query.trim()
    resetSearch()

    if (selectedItem) {
      focusDocumentBody()
      // open the selected product from the suggested search
      navigate(withQueryAndScope(selectedItem.href, location))
    } else if (trimmedQuery.length > 0) {
      focusDocumentBody()
      // open the search with the search query
      navigate(withQueryAndScope(generateSearchUrl(trimmedQuery), location))
    } else {
      // the query is empty on submission, so show an error
      setEmptyQueryError(true)
    }
  }

  const moveSelection = (delta: number) => {
    if (!results || results.size === 0) return

    if (Number.isInteger(selected)) {
      updateSelection(clamp(selected + delta, 0, results.size - 1))
    } else {
      updateSelection(clamp(delta > 0 ? 0 : results.size - 1, 0, results.size - 1))
    }
  }

  const clearEmptyQueryError = () => setEmptyQueryError(false)

  const handleChange = (event) => {
    const { value } = event.target

    setQuery(value)
    updateQuery(value)
  }

  const handleKeyDown = (event) => {
    switch (event.keyCode) {
      case KeyCodes.ESCAPE:
        resetSearch()
        clearEmptyQueryError()
        break
      case KeyCodes.RETURN:
        event.preventDefault()
        getResults()
        break
      case KeyCodes.UP:
        event.preventDefault()
        moveSelection(-1)
        break
      case KeyCodes.DOWN:
        event.preventDefault()
        moveSelection(+1)
        break
      default:
        clearEmptyQueryError()
        break
    }
  }

  const handleBlur = () => clearEmptyQueryError()

  // extend the className from the template with the error-class if needed
  const classes = cc([
    className,
    {
      'empty-error': emptyQueryError,
    },
  ])

  // use the placeholder the theme developer hands over as long as no error happens
  if (emptyQueryError) {
    placeholder = t('components.productSearchComponent.searchInputField.validationMessages.noEmptySubmit')
  }

  const htmlAttributes = omit(rest, [
    'locale',
    'resetSearch',
    'updateSearch',
    'updateSelection',
    'namespace',
    'store',
    'className',
    'i18n',
    'maxSuggestedItems',
    'placeholder',
    'results',
    'selected',
    'tReady',
  ])

  return (
    <input
      {...htmlAttributes}
      ref={ref}
      type="text"
      maxLength={200}
      value={query || ''}
      className={classes}
      placeholder={placeholder}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  )
}

type ResultsListProps = {
  renderItems: (items: Frontend.SuggestedSearchProduct[]) => ReactElement
}

export function ResultsList(props: Readonly<ResultsListProps>) {
  const namespace = useNamespace()

  const results = useSelector<State, ImmutableList | undefined>((state) =>
    state.getIn(['view', 'suggestedSearch', namespace, 'results']),
  )
  const selected = useSelector<State, number>((state) =>
    state.getIn(['view', 'suggestedSearch', namespace, 'selected']),
  )

  if (!results || results.size === 0) return null

  return props.renderItems(
    results.toJS().map((item, index) => ({
      ...item,
      active: index === selected,
    })),
  )
}
